import {action, observable, runInAction} from 'mobx'
import {ICandidate, IImageData, INewCandidate} from '../types/candidate'
import {adminGetCandidate, createCandidate, getCandidate, updateCandidate, uploadCandidatePhoto} from './api/candidate'
import {NotificationStore} from './NotificationStore'
import {QuestionStore} from './QuestionStore'
import {AnswerStore} from './AnswerStore'

export class CandidateStore {
  @observable
  candidate?: ICandidate = undefined
  tunnusCandidate?: INewCandidate
  notifications: NotificationStore
  questions: QuestionStore
  answers: AnswerStore
  consistuencyName = ''

  constructor(notifications: NotificationStore, questions: QuestionStore, answers: AnswerStore) {
    this.notifications = notifications
    this.questions = questions
    this.answers = answers
  }

  @action
  async updateCandidate(updatedCandidate: Omit<ICandidate, 'election_id'>, electionId: number): Promise<ICandidate> {
    const {candidate, questions, answers} = await updateCandidate(electionId, updatedCandidate)
    runInAction(() => {
      this.candidate = candidate
      this.questions.setQuestions(questions)
      this.answers.setAnswers(answers)
    })
    return this.candidate!
  }

  @action
  async uploadPhoto(candidate: ICandidate, imageData: IImageData): Promise<ICandidate> {
    try {
      const updated = await uploadCandidatePhoto(candidate, imageData)
      runInAction(() => {
        this.candidate = updated
      })
    } catch (error: any) {
      let notificationMessage = 'Virhe kuvan tallentamisessa.'
      if (error.response && error.response.data && error.response.data.message) {
        if (error.response.data.message.includes('extract_area:')) {
          notificationMessage = `${notificationMessage} Kuvan rajaaminen epäonnistui. Yritä rajata kuva uudelleen.`
        } else if (error.response.data.message.includes('invalid type')) {
          notificationMessage = `${notificationMessage} Tiedosto on väärää tyyppiä. Sallitut tiedostomuodot ovat .jpg, .jpeg ja .png.`
        }
      }

      runInAction(() => {
        this.notifications.showNotification({message: notificationMessage, type: 'error', sticky: true})
      })
    }
    return this.candidate!
  }

  @action
  async adminGetCandidate(candidateId: number | string): Promise<void> {
    try {
      const res = await adminGetCandidate(candidateId)
      this.setCandidate(res)
    } catch (error) {
      console.error('Error fetching candidate', error) // eslint-disable-line no-console
    }
  }

  @action
  async getCandidate(candidateId: string, electionId: string): Promise<ICandidate | undefined> {
    try {
      const candidate = await getCandidate(candidateId, electionId)
      return candidate
    } catch (error) {
      console.log(error) // eslint-disable-line no-console
      throw new Error('Error fetching candidate')
    }
  }

  @action
  async createCandidate(candidateId: string, electionId: string, newCandidate: INewCandidate): Promise<ICandidate | undefined> {
    try {
      const candidate = await createCandidate(candidateId, electionId, newCandidate)

      return candidate
    } catch (error) {
      console.error('Error checking candidate registration', error) // eslint-disable-line no-console
    }
  }

  @action
  public async fetchAndSetCandidate(candidateId: string, electionId: string) {
    let candidate = await this.getCandidate(candidateId, electionId)

    const tunnusCandidate = this.tunnusCandidate
    if (!candidate && tunnusCandidate) {
      candidate = await this.createCandidate(candidateId, electionId, tunnusCandidate)
    }

    if (!candidate) {
      throw new Error('Candidate not found')
    }

    this.setCandidate(candidate)
  }

  @action
  setCandidate(candidate: ICandidate) {
    this.candidate = candidate
  }

  @action
  setTunnusCandidate(candidate: INewCandidate) {
    this.tunnusCandidate = candidate
  }

  @action setConstituencyName(cName: string) {
    this.consistuencyName = cName
  }
}
